/**
 * [Insider+] Marketing sidebar shown on the Insider+ registration variant.
 */

import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import themeColors from '../../assets/styles/themeColors';
import Logo from '../../components/Logo/Logo';
import config from '../../config';
import Paragraph from '../../components/Paragraph/Paragraph';
import { variables } from '../../assets/styles/variables';
import useWindowSize from '../../util/hooks/useIsMobileHook';
import GoldCoins from '../../assets/images/gold-coins-1_1.png';
import SilverCoins from '../../assets/images/silver-coins-1_1.png';
import { mediaBelow, mediaBetween, mediaUp } from '../../assets/styles/helper';
import GlobalStyle from '../../assets/styles/base';
import RegistrationPopup from './RegistrationPopup';

const LeftPaneContainer = styled.div`
  display: flex;
  flex-direction: column;
  border-radius: ${({ isMobile }) => (isMobile ? '' : '8px')};
  overflow: clip;
  background: ${({ isMobile }) =>
    isMobile ? themeColors.colorSidebarBegin : 'none'};
  padding-bottom: ${({ isMobile }) => (isMobile ? '140px' : '0')};
  min-height: ${({ isMobile }) => (isMobile ? '100vh' : 'unset')};
  ${mediaBelow(variables.breakpoints.bpLgX)} {
    width: 100%;
  }
  ${mediaBetween(variables.breakpoints.bp1280, variables.breakpoints.bp1366)} {
    width: 534px;
  }
  ${mediaBetween(variables.breakpoints.bp1366, variables.breakpoints.bpXxxl)} {
    width: 534px;
  }
  ${mediaBetween(variables.breakpoints.bpXxxl, variables.breakpoints.bp1600)} {
    width: 612px;
  }
  ${mediaBetween(variables.breakpoints.bp1600, variables.breakpoints.bp1920)} {
    width: 750px;
  }
  ${mediaUp(variables.breakpoints.bp1920)} {
    width: 850px;
  }
`;

const RegistrationSidebar = styled.div`
  background: ${themeColors.colorPrimary};
  padding: 16px 24px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: -2px;

  ${mediaUp(variables.breakpoints.bpXxxl)} {
    gap: 16px;
  }

  ${mediaBelow(variables.breakpoints.bpLg)} {
    padding: 16px;
    flex: 1;
  }

  svg {
    color: ${themeColors.colorHeaderLogoLight};
    height: calc(1.75rem * var(--legacy-scale, 1));
    object-fit: contain;
  }
`;

const CardsContainer = styled.div`
  display: grid;
  grid-template-columns: ${({ isMobile }) => (isMobile ? '1fr' : '1fr 1fr')};
  grid-gap: 16px;
`;

const RegistrationCardTypeOne = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: ${themeColors.colorRegCardBackground};
  padding: calc(1rem * var(--legacy-scale, 1));
  border-radius: ${variables.borderRadius.borderRadiusMd};

  ${mediaBelow(variables.breakpoints.bpLg)} {
    height: 125px;
  }
`;

const TitleContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin: calc(1.5rem * var(--legacy-scale, 1)) 0 0rem 0;
  max-height: 358px;

  ${mediaUp(variables.breakpoints.bpXxxl)} {
    margin: calc(3rem * var(--legacy-scale, 1)) 0 0rem 0;
  }
`;

const ValuePropCard = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  padding: calc(1rem * var(--legacy-scale, 1));
  background: ${themeColors.colorRegCardBackground};
  border: 0.6px solid ${themeColors.colorCardTypeTwoBorder};
  border-radius: ${variables.borderRadius.borderRadiusMd};

  ${mediaUp(variables.breakpoints.bpXxxl)} {
    padding: calc(1.25rem * var(--legacy-scale, 1))
      calc(1rem * var(--legacy-scale, 1));
  }

  ${mediaBelow(variables.breakpoints.bpLg)} {
    padding: calc(1rem * var(--legacy-scale, 1));
  }
`;

const GOLD_COIN_SIZE_SM = '120px';
const GOLD_COIN_SIZE_MD = '145px';
const GOLD_COIN_SIZE_LG = '190px';

const SILVER_COIN_SIZE_SM = '100px';
const SILVER_COIN_SIZE_MD = '120px';
const SILVER_COIN_SIZE_LG = '155px';

const getGoldCoinSize = (width) => {
  if (width < variables.breakpoints.bpXl) return GOLD_COIN_SIZE_SM;
  if (width < variables.breakpoints.bp1366) return GOLD_COIN_SIZE_MD;
  return GOLD_COIN_SIZE_LG;
};

const getSilverCoinSize = (width) => {
  if (width < variables.breakpoints.bpXl) return SILVER_COIN_SIZE_SM;
  if (width < variables.breakpoints.bp1366) return SILVER_COIN_SIZE_MD;
  return SILVER_COIN_SIZE_LG;
};

const ImageContainer = styled.img`
  flex-shrink: 0;
  height: ${({ height }) => height || '166px'};
  width: ${({ width }) => width || '166px'};
  object-fit: contain;
`;

const FixedPopupWrapper = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  background: ${themeColors.colorWhite};
  padding: calc(0.5rem * var(--legacy-scale, 1))
    calc(1rem * var(--legacy-scale, 1));
`;

const CoinsContainer = styled.div`
  display: flex;
  flex: 1;
  justify-content: space-between;
  align-items: flex-end;
  padding: 0;
  border-radius: ${({ isMobile }) => (isMobile ? '' : '0 0 8px 8px')};
  background: ${themeColors.colorSidebarBegin};
`;

const getTitleFontSize = (width, smallFont, mediumFont, largeFont) => {
  if (width < variables.breakpoints.bpLg) return smallFont;
  if (width <= variables.breakpoints.bpXxxl) return mediumFont;
  return largeFont;
};

// Headline/subheadline need a tier the coarser getTitleFontSize doesn't have:
// the sidebar is only 612px wide from 1440-1600px (vs 750-850px above that),
// too narrow for the same largeFont used above 1600px.
const getHeroFontSize = (
  width,
  smallFont,
  mediumFont,
  midLargeFont,
  largeFont,
) => {
  if (width < variables.breakpoints.bpLg) return smallFont;
  if (width <= variables.breakpoints.bpXxxl) return mediumFont;
  if (width <= variables.breakpoints.bp1600) return midLargeFont;
  return largeFont;
};

const InsiderPlusRegistrationSidebar = ({ isOpen, setIsOpen }) => {
  const { t } = useTranslation();
  const windowSize = useWindowSize();
  const isMobile = windowSize.width < variables.breakpoints.bpLg;

  return (
    <>
      <GlobalStyle />
      <LeftPaneContainer isMobile={isMobile}>
        <RegistrationSidebar isGS={config?.brokerTheme === 'GoldSilver'}>
          <Logo />
          <TitleContainer>
            <Paragraph
              fontWeight={1000}
              fontSize={getHeroFontSize(windowSize.width, 20, 20, 28, 44)}
              color={themeColors.colorWhite}
              usePx
            >
              {t('insiderPlus.sidebar.headline')}
            </Paragraph>

            <Paragraph
              bold
              fontSize={getHeroFontSize(windowSize.width, 14, 15, 18, 36)}
              color={themeColors.colorRegCardSubtitleText}
              usePx
            >
              {t('insiderPlus.sidebar.subheadline')}
            </Paragraph>
          </TitleContainer>

          <CardsContainer isMobile={isMobile}>
            <RegistrationCardTypeOne>
              <Paragraph
                fontWeight={1000}
                fontSize={getTitleFontSize(windowSize.width, 34, 28, 40)}
                color={themeColors.colorWhite}
                usePx
              >
                {t('insiderPlus.sidebar.stat1Value')}
              </Paragraph>
              <Paragraph
                bold
                color={themeColors.colorRegCardSubtitleText}
                fontSize={getTitleFontSize(windowSize.width, 18, 16, 20)}
                centerText
                usePx
              >
                {t('insiderPlus.sidebar.stat1Label')}
              </Paragraph>
            </RegistrationCardTypeOne>
            <RegistrationCardTypeOne>
              <Paragraph
                fontWeight={1000}
                fontSize={getTitleFontSize(windowSize.width, 34, 28, 40)}
                color={themeColors.colorWhite}
                usePx
              >
                {t('insiderPlus.sidebar.stat2Value')}
              </Paragraph>
              <Paragraph
                bold
                color={themeColors.colorRegCardSubtitleText}
                fontSize={getTitleFontSize(windowSize.width, 18, 16, 20)}
                centerText
                usePx
              >
                {t('insiderPlus.sidebar.stat2Label')}
              </Paragraph>
            </RegistrationCardTypeOne>
          </CardsContainer>

          <ValuePropCard>
            <Paragraph
              color={themeColors.colorWhite}
              fontSize={getTitleFontSize(windowSize.width, 14, 16, 20)}
              fontWeight={500}
              lineHeight={
                isMobile ? 'calc(1.5rem * var(--legacy-scale, 1))' : ''
              }
              usePx
            >
              {t('insiderPlus.sidebar.valueProp')}
            </Paragraph>
          </ValuePropCard>

          <Paragraph
            bold
            color={
              isMobile
                ? themeColors.colorCardTypeTwoBorder
                : themeColors.colorWhite
            }
            fontSize={16}
            usePx
          >
            {t('insiderPlus.sidebar.footer')}
          </Paragraph>
        </RegistrationSidebar>

        <CoinsContainer>
          {!isMobile && (
            <>
              <ImageContainer
                src={GoldCoins}
                alt="gold-coins"
                height={getGoldCoinSize(windowSize.width)}
                width={getGoldCoinSize(windowSize.width)}
                borderRadius="0 0 0 8px"
              />

              <ImageContainer
                src={SilverCoins}
                alt="silver-coins"
                height={getSilverCoinSize(windowSize.width)}
                width={getSilverCoinSize(windowSize.width)}
                borderRadius="0 0 8px 0"
              />
            </>
          )}
        </CoinsContainer>
        {isMobile && isOpen && (
          <FixedPopupWrapper>
            <RegistrationPopup isOpen={isOpen} setIsOpen={setIsOpen} />
          </FixedPopupWrapper>
        )}
      </LeftPaneContainer>
    </>
  );
};

InsiderPlusRegistrationSidebar.propTypes = {
  isOpen: PropTypes.bool,
  setIsOpen: PropTypes.func,
};

export default InsiderPlusRegistrationSidebar;
